import React, { useMemo, useState } from "react";

import ConsignmentReportSection from "../components/reports/ConsignmentReportSection";
import CustomReportsSection from "../components/reports/CustomReportsSection";
import InventoryOptimizerSection from "../components/reports/InventoryOptimizerSection";
import LoadingSpinner from "../components/LoadingSpinner";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import { useScopedBoxes } from "../hooks/useScopedBoxes";
import { fmtUtc, fmtWorkCenter } from "../lib/format";
import {
  type MinMaxReport,
  type StockoutReport,
  fetchInventoryMinmax,
  fetchStockoutAging,
} from "../services/reportsApi";

const ALL = "__all__";

type ReportType = "stockout" | "minmax" | "optimizer" | "consignment";

type Result =
  | { type: "stockout"; report: StockoutReport; label: string }
  | { type: "minmax"; report: MinMaxReport; label: string };

/** Human age from an ISO timestamp ("5d", "3h", "just now"). */
function ageLabel(iso: string | null): string {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return "just now";
  const days = Math.floor(ms / 86_400_000);
  if (days >= 1) return `${days}d`;
  return `${Math.floor(ms / 3_600_000)}h`;
}

const esc = (v: unknown): string => `"${String(v ?? "").replace(/"/g, '""')}"`;

type MmStatus = "NO DATA" | "OUT" | "LOW" | "OK";

/** Status for an Inventory-with-Min/Max row. NO DATA when the box's latest
 *  scan read 0 tags (on_hand=0 then means "blind scan", not real zero). */
function minmaxStatus(l: {
  has_scan_data: boolean;
  on_hand: number;
  min_qty: number;
}): MmStatus {
  if (!l.has_scan_data) return "NO DATA";
  if (l.min_qty > 0 && l.on_hand === 0) return "OUT";
  if (l.on_hand > 0 && l.on_hand < l.min_qty) return "LOW";
  return "OK";
}

/**
 * Reports — most reports are visible to every role (customer / vendor /
 * platform), scoped server-side to the user's boxes:
 *   • Stockout Aging — min-assigned items now at zero on-hand, aged from the
 *     scan they were last seen in (no transaction data needed). Allows "All
 *     locations" (tidy result set).
 *   • Inventory with Min & Max — every min-assigned item at a location with its
 *     on-hand. Requires a specific location (fleet-wide is tens of thousands).
 *   • Inventory Optimizer — platform + vendor only (customers see refill via
 *     the Refill page; optimizer surfaces cost / drift signals for buyers
 *     and suppliers). Vendor users only see items they supply.
 * Sales by Vendor is listed but disabled until consumption data exists.
 */
export const ReportsPage: React.FC = () => {
  const { token, role } = useAuth();
  const { snapshot } = useScopedBoxes();
  const showOptimizer = role !== "customer";
  const [reportType, setReportType] = useState<ReportType>("stockout");
  const [location, setLocation] = useState<string>("");
  const [result, setResult] = useState<Result | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Value is the workcenter name (what the report API is keyed by); the
  // label carries the smartbox code(s) in parentheses so an operator can
  // tell duplicate-workcenter locations apart. Multi-box workcenters
  // (allowed since migration 075's per-smartbox min/max) get their codes
  // joined: "Nabors WC 00X30 (SB040, SB041)".
  const locations = useMemo(() => {
    const codesByWc = new Map<string, Set<string>>();
    for (const b of snapshot?.boxes ?? []) {
      if (!b.work_center) continue;
      if (!codesByWc.has(b.work_center)) codesByWc.set(b.work_center, new Set());
      if (b.smartbox_code) codesByWc.get(b.work_center)!.add(b.smartbox_code);
    }
    return Array.from(codesByWc.entries())
      .map(([wc, codes]) => ({
        value: wc,
        label: codes.size > 0
          ? `${wc} (${Array.from(codes).sort().join(", ")})`
          : wc,
      }))
      .sort((a, b) => a.value.localeCompare(b.value));
  }, [snapshot]);

  const allowAll = reportType === "stockout"; // min/max requires a location

  const pickType = (t: ReportType): void => {
    setReportType(t);
    setResult(null);
    setError(null);
    setLocation("");
  };

  const load = (loc: string): void => {
    if (!token || !loc) return;
    setLoading(true);
    setResult(null);
    setError(null);
    const label = loc === ALL ? "All locations" : loc;
    const wc = loc === ALL ? undefined : loc;
    const p =
      reportType === "stockout"
        ? fetchStockoutAging(token, wc).then((report) =>
            setResult({ type: "stockout", report, label }),
          )
        : fetchInventoryMinmax(token, loc).then((report) =>
            setResult({ type: "minmax", report, label }),
          );
    p.catch((e) => setError(e instanceof Error ? e.message : String(e))).finally(() =>
      setLoading(false),
    );
  };

  const onPickLocation = (loc: string): void => {
    setLocation(loc);
    load(loc);
  };

  const exportCsv = (): void => {
    if (!result) return;
    let rows: string[];
    let fname: string;
    if (result.type === "stockout") {
      rows = ["Smartbox,WorkCenter,SKU,Name,Min,Max,StockoutSince,Age"];
      for (const l of result.report.lines) {
        rows.push(
          [
            l.smartbox_code ?? l.scu_name,
            l.workcenter_name ?? "",
            l.sku,
            l.name,
            l.min_qty,
            l.suggest_qty,
            l.stockout_since_utc ?? "",
            ageLabel(l.stockout_since_utc),
          ]
            .map(esc)
            .join(","),
        );
      }
      fname = "stockout-aging";
    } else {
      rows = ["Smartbox,WorkCenter,SKU,Name,On hand,Min,Max,Status"];
      for (const l of result.report.lines) {
        const status = minmaxStatus(l);
        rows.push(
          [
            l.smartbox_code ?? l.scu_name,
            l.workcenter_name ?? "",
            l.sku,
            l.name,
            l.has_scan_data ? l.on_hand : "",
            l.min_qty,
            l.suggest_qty,
            status,
          ]
            .map(esc)
            .join(","),
        );
      }
      fname = "inventory-minmax";
    }
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${fname}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const seg = (t: ReportType, label: string): React.ReactNode => (
    <button
      type="button"
      onClick={() => pickType(t)}
      className={
        "px-3 py-1.5 text-sm " +
        (reportType === t ? "bg-primary-500 text-white" : "bg-white text-secondary-700")
      }
    >
      {label}
    </button>
  );

  return (
    <div className="p-4 md:p-6">
      <header className="mb-4">
        <h1 className="text-xl font-bold">Reports</h1>
        <p className="text-xs text-secondary-500 mt-1">
          Pick a report and a location to load it.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex rounded-md border border-secondary-300 overflow-hidden">
          {seg("stockout", "Stockout Aging")}
          {seg("minmax", "Inventory + Min/Max")}
          {showOptimizer && seg("optimizer", "Inventory Optimizer")}
          {seg("consignment", "Weekly Report")}
          <span
            className="px-3 py-1.5 text-sm bg-secondary-50 text-secondary-400 cursor-not-allowed"
            title="Requires consumption history (coming later)"
          >
            Sales by Vendor
          </span>
        </div>

        {reportType !== "optimizer" && reportType !== "consignment" && (
          <>
            <select
              value={location}
              onChange={(e) => onPickLocation(e.target.value)}
              className="px-3 py-2 min-h-[40px] border border-secondary-300 rounded-md text-sm bg-white"
            >
              <option value="" disabled>
                Select a location…
              </option>
              {allowAll && <option value={ALL}>All locations</option>}
              {locations.map((loc) => (
                <option key={loc.value} value={loc.value}>
                  {loc.label}
                </option>
              ))}
            </select>

            <Button variant="outline" size="sm" onClick={exportCsv} disabled={!result}>
              Export CSV
            </Button>
          </>
        )}
      </div>

      {reportType !== "optimizer" && reportType !== "consignment" && (
        <p className="text-xs text-secondary-500 mb-3">
          {reportType === "stockout"
            ? "Items with a min/max that are at zero on-hand, aged from the scan they were last seen in."
            : "Every item with a min/max assigned at the location, with current on-hand. Pick a specific location."}
          {result && ` · ${result.label}: ${result.report.total.toLocaleString()} row${result.report.total === 1 ? "" : "s"}.`}
        </p>
      )}
      {reportType === "optimizer" && (
        <p className="text-xs text-secondary-500 mb-3">
          Lead-time-aware refill + excess recommendations. Picks a sensible
          target days-on-hand from each item's lead time, compares against
          current on-hand, and surfaces a refill quantity, excess on hand,
          and an "update Min/Max?" flag where the recommendation diverges
          from the configured min/max by more than the threshold.
        </p>
      )}

      {error && reportType !== "optimizer" && reportType !== "consignment" && (
        <div className="mb-3 text-sm text-error-700 bg-error-50 px-3 py-2 rounded">{error}</div>
      )}

      {reportType === "optimizer" ? (
        <InventoryOptimizerSection locations={locations} />
      ) : reportType === "consignment" ? (
        <ConsignmentReportSection />
      ) : location === "" && !loading ? (
        <div className="text-sm text-secondary-500 bg-white p-6 rounded-md border border-secondary-200">
          Choose a location above{allowAll ? " (or “All locations”)" : ""} to load this report.
        </div>
      ) : loading ? (
        <LoadingSpinner label="building report" />
      ) : error || !result ? null : result.type === "stockout" ? (
        result.report.total === 0 ? (
          <div className="text-sm text-secondary-700 bg-success-50 border border-success-200 p-6 rounded-md">
            ✓ No stockouts — every min-assigned item has stock on hand.
          </div>
        ) : (
          <div className="bg-white border border-secondary-200 rounded-md overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary-50 text-secondary-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-3 py-2">Smartbox / Work center</th>
                  <th className="text-left px-3 py-2">Part / SKU</th>
                  <th className="text-right px-3 py-2">Min</th>
                  <th className="text-left px-3 py-2">Stocked out since</th>
                  <th className="text-right px-3 py-2">Age</th>
                </tr>
              </thead>
              <tbody>
                {result.report.lines.map((l) => (
                  <tr key={`${l.scu_name}:${l.item_id}`} className="border-t border-secondary-100">
                    <td className="px-3 py-2">
                      <span className="font-semibold">{l.smartbox_code ?? "—"}</span>
                      {l.workcenter_name && (
                        <span className="text-secondary-600"> · {fmtWorkCenter(l.workcenter_name)}</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-mono text-secondary-900">{l.sku ?? "—"}</div>
                      {l.name && <div className="text-xs text-secondary-500">{l.name}</div>}
                    </td>
                    <td className="text-right px-3 py-2 text-secondary-600">{l.min_qty}</td>
                    <td className="px-3 py-2 text-secondary-600">{fmtUtc(l.stockout_since_utc)}</td>
                    <td className="text-right px-3 py-2 font-semibold text-error-700">
                      {ageLabel(l.stockout_since_utc)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        <>
          {result.report.lines.some((l) => !l.has_scan_data) && (
            <div className="mb-3 text-sm text-warning-900 bg-warning-50 border border-warning-300 px-3 py-2 rounded">
              ⚠ This location's latest scan read 0 tags (scanner blind / box
              down), so on-hand is unknown — shown as “—”, not zero. These items
              won't appear in Stockout Aging either, since there's no usable scan
              to age from.
            </div>
          )}
          <div className="bg-white border border-secondary-200 rounded-md overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary-50 text-secondary-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-3 py-2">Smartbox / Work center</th>
                  <th className="text-left px-3 py-2">Part / SKU</th>
                  <th className="text-right px-3 py-2">On hand</th>
                  <th className="text-right px-3 py-2">Min</th>
                  <th className="text-right px-3 py-2">Max</th>
                  <th className="text-left px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {result.report.lines.map((l) => {
                  const status = minmaxStatus(l);
                  return (
                    <tr
                      key={`${l.scu_name}:${l.item_id}`}
                      className={
                        "border-t border-secondary-100 " +
                        (status === "OUT" ? "bg-error-50" : "")
                      }
                    >
                      <td className="px-3 py-2">
                        <span className="font-semibold">{l.smartbox_code ?? "—"}</span>
                        {l.workcenter_name && (
                          <span className="text-secondary-600"> · {fmtWorkCenter(l.workcenter_name)}</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-mono text-secondary-900">{l.sku ?? "—"}</div>
                        {l.name && <div className="text-xs text-secondary-500">{l.name}</div>}
                      </td>
                      <td className="text-right px-3 py-2 font-semibold">
                        {l.has_scan_data ? l.on_hand : "—"}
                      </td>
                      <td className="text-right px-3 py-2 text-secondary-600">{l.min_qty}</td>
                      <td className="text-right px-3 py-2 text-secondary-600">
                        {l.suggest_qty ?? "—"}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={
                            "text-[11px] px-1.5 py-0.5 rounded " +
                            (status === "OUT"
                              ? "bg-error-100 text-error-700"
                              : status === "LOW"
                                ? "bg-warning-100 text-warning-700"
                                : status === "NO DATA"
                                  ? "bg-secondary-200 text-secondary-600"
                                  : "bg-success-100 text-success-700")
                          }
                        >
                          {status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      <CustomReportsSection />
    </div>
  );
};

export default ReportsPage;
